import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import { mdiBell, mdiBroadcast, mdiRefresh } from '@mdi/js';
import * as React from 'react';
import { NotificationsFilters } from './components/NotificationsFilters';
import { NotificationsTable } from './components/NotificationsTable';
import { SendNotificationModal } from './components/SendNotificationModal';
import {
  NotificationSource,
  type NotificationType,
  useNotifications,
} from './hooks/useNotifications';

export const NotificationsPage: React.FC = () => {
  const [page, setPage] = React.useState(1);
  const [typeFilter, setTypeFilter] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const limit = 20;

  const { data, isLoading, refetch } = useNotifications({
    page,
    limit,
    type: typeFilter as NotificationType | undefined,
    source: NotificationSource.admin,
    grouped: true,
  });

  const handleTypeChange = (type: string) => {
    setTypeFilter(type);
    setPage(1);
  };

  const handleClearFilters = () => {
    setTypeFilter('');
    setPage(1);
  };

  return (
    <DashboardLayout>
      <ProtectedRoute action='read' subject='Notification'>
        <div className='space-y-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
            <div>
              <h1 className='text-3xl font-bold text-grey-900 flex items-center'>
                <Icon path={mdiBell} size={32} className='mr-3' />
                Notifications
              </h1>
              <p className='text-grey-600 mt-2'>
                Send and manage push notifications to users
              </p>
            </div>
            <div className='flex flex-wrap items-center gap-3'>
              <div className='text-sm text-grey-600'>
                {data?.total || 0} total notifications
              </div>
              <Button
                icon={mdiRefresh}
                color='light'
                title='Refresh'
                onClick={() => refetch()}
                loading={isLoading}
              />
              <Button
                label='Send Notification'
                icon={mdiBroadcast}
                color='primary'
                onClick={() => setIsModalOpen(true)}
              />
            </div>
          </div>

          <NotificationsFilters
            selectedType={typeFilter}
            onTypeChange={handleTypeChange}
            onClearFilters={handleClearFilters}
          />

          <NotificationsTable
            notifications={data?.notifications || []}
            isLoading={isLoading}
          />

          {(data?.totalPages || 1) > 1 && (
            <Pagination
              currentPage={data?.currentPage || 1}
              totalPages={data?.totalPages || 1}
              onPageChange={setPage}
              totalItems={data?.total || 0}
              itemsPerPage={limit}
            />
          )}
        </div>

        <SendNotificationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          broadcastOnly
        />
      </ProtectedRoute>
    </DashboardLayout>
  );
};
